<template>
    <div v-show="true" class="project-management-panel">
        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner"></div>
        </div>
        <div class="project-panel-inner" :class="{ 'disabled': isLoading }">
            <div class="project-selector">
                {{ project.project_name }}
            </div>

            <div v-if="filteredPages.length === 0" class="empty-page-message">
                There is nothing here, add new pages to create.
            </div>

            <ul v-else class="page-tree">
                <PageTree v-for="page in filteredPages" :key="page.id" :node="page" :siblings="filteredPages"
                    :selectedPage="selectedPage" @select="selectPage" @addChild="addChildPage" @move-up="movePageUp"
                    @move-down="movePageDown" @delete-page="requestDeletePage" />
            </ul>

            <button class="btn btn-full-width" @click="showAddPagePopup">
                Add New Page
            </button>
        </div>
    </div>
    <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showConfirmButton="showInfoPopupConfirmButton"
        :showCancelButton="showInfoPopupCancelButton" :confirmText="infoConfirmText" :cancelText="infoCancelText"
        @confirm="onInfoPopupConfirm" @cancel="onInfoPopupCancel">
        <p>{{ infoMessage }}</p>
    </InfoPopup>
    <InputPopup v-model="showInputPopup" :title="popupTitle" :fields="popupFields" @confirm="onInputPopupConfirm" />
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue';
import { requireAuth } from '@/api/auth';
import {
    getPagesByUserId,
    createPageByUserId,
    modifyPageName,
    swapPageOrder,
    deletePageByUserId
} from '@/api/ttsProjects';
import PageTree from '@/components/TTS/PageTree.vue';
import InputPopup from '@/components/common/InputPopup.vue';
import InfoPopup from '@/components/common/InfoPopup.vue';

export default defineComponent({
    name: 'ProjectPanel',
    props: {
        project: {
            type: Object,
            default: () => ({ project_id: null, project_name: '' })
        },
    },
    components: { PageTree, InfoPopup, InputPopup },
    emits: ['select', 'toggle', 'select-name'],
    setup(props, { emit }) {

        const pages = ref([]);
        const isLoading = ref(false);
        const selectedPage = ref(null);

        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showInfoPopupConfirmButton = ref(true);
        const showInfoPopupCancelButton = ref(true);
        const infoConfirmText = ref('Confirm');
        const infoCancelText = ref('Cancel');

        const showInputPopup = ref(false);
        const popupTitle = ref('');
        const popupFields = ref([]);
        let popupAction = null;
        let popupNode = null;
        let pendingDeleteNode = null;

        // Load and build the page tree
        async function loadPages() {
            isLoading.value = true;
            try {
                let data = await getPagesByUserId();
                data = data.filter(
                    p => p.project_id === props.project.project_id && p.status !== 'deleted'
                );
                const nodes = data.map(p => ({
                    ...p,
                    id: p.page_id,
                    name: p.page_name,
                    children: []
                }));
                const lookup = {};
                nodes.forEach(n => (lookup[n.id] = n));
                nodes.forEach(n => {
                    if (n.parent_page_id && lookup[n.parent_page_id]) {
                        lookup[n.parent_page_id].children.push(n);
                    }
                });
                const roots = nodes
                    .filter(n => !n.parent_page_id || n.parent_page_id === 'None')
                    .sort((a, b) => a.page_order - b.page_order);
                const sortChildren = node => {
                    node.children.sort((a, b) => a.page_order - b.page_order);
                    node.children.forEach(sortChildren);
                };
                roots.forEach(sortChildren);
                pages.value = roots;
                const preservedPage = findPageById(roots, selectedPage.value);
                const nextPage = preservedPage || roots[0];
                if (nextPage) {
                    selectedPage.value = nextPage.id;
                    emit('select', selectedPage.value);
                    emit('select-name', { id: selectedPage.value, name: nextPage.name });
                } else {
                    selectedPage.value = null;
                    emit('select', 0);
                    emit('select-name', { id: 0, name: '' });
                }
            } catch (err) {
                console.error('Failed to load pages:', err);
            } finally {
                isLoading.value = false;
            }
        }

        function findPageById(nodes, targetId) {
            if (!targetId) return null;
            for (const node of nodes) {
                if (node.id === targetId) return node;
                const childMatch = findPageById(node.children || [], targetId);
                if (childMatch) return childMatch;
            }
            return null;
        }

        function selectPage(id, name) {
            selectedPage.value = id;
            // if name not provided (backward compat from other emitters), search it
            if (!name) {
                const findName = (nodes, target) => {
                    for (const n of nodes) {
                        if (n.id === target) return n.name;
                        if (n.children && n.children.length) {
                            const r = findName(n.children, target);
                            if (r) return r;
                        }
                    }
                    return null;
                };
                name = findName(pages.value, id);
            }
            emit('select', id);
            emit('select-name', { id, name });
        }

        function showAddPagePopup() {
            if (!requireAuth('ProjectPanel.addPage')) return;
            popupTitle.value = 'Enter name for the new page';
            popupFields.value = [
                { key: 'name', label: 'Page Name', default: '' }
            ];
            popupAction = 'addPage';
            showInputPopup.value = true;
        }
        async function addPage(name) {
            if (!name || !name.name || !name.name.trim()) return;
            isLoading.value = true;
            try {
                const { page_id } = await createPageByUserId(
                    props.project.project_id,
                    'None'
                );
                await modifyPageName(
                    props.project.project_id,
                    page_id,
                    name.name.trim()
                );
                await loadPages();
            } catch (err) {
                infoTitle.value = 'Error!';
                infoMessage.value = 'Failed to create page. Please try again.';
                showInfoPopupConfirmButton.value = true;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                infoConfirmText.value = 'OK';
            } finally {
                isLoading.value = false;
            }
        }


        function showAddChildPagePopup(node) {
            if (!requireAuth('TTS.addChildPage')) return;
            popupTitle.value = `Enter name for new subpage of "${node.name}"`;
            popupFields.value = [
                { key: 'name', label: 'Subpage Name', default: '' }
            ];
            popupAction = 'addChildPage';
            popupNode = node;
            showInputPopup.value = true;
        }
        async function addChildPage(node, name) {
            if (!name || !name.name || !name.name.trim()) return;
            isLoading.value = true;
            try {
                const { page_id } = await createPageByUserId(
                    props.project.project_id,
                    node.id
                );
                await modifyPageName(
                    props.project.project_id,
                    page_id,
                    name.name.trim()
                );
                await loadPages();
            } catch (err) {
                infoTitle.value = 'Error!';
                infoMessage.value = 'Failed to create subpage. Please try again.';
                showInfoPopupConfirmButton.value = true;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                infoConfirmText.value = 'OK';
            } finally {
                isLoading.value = false;
            }
        }
        function addChildPageHandler(node) {
            showAddChildPagePopup(node);
        }
        async function onInputPopupConfirm(values) {
            if (popupAction === 'addPage') {
                await addPage(values);
            } else if (popupAction === 'addChildPage' && popupNode) {
                await addChildPage(popupNode, values);
            }
            popupAction = null;
            popupNode = null;
        }

        function resetInfoPopupButtons() {
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = true;
            infoConfirmText.value = 'Confirm';
            infoCancelText.value = 'Cancel';
        }

        function requestDeletePage(node) {
            if (!requireAuth('ProjectPanel.deletePage')) return;
            if (node.children && node.children.some(child => child.status !== 'deleted')) {
                infoTitle.value = 'Warning!';
                infoMessage.value = 'This page has subpages. Please delete them first.';
                showInfoPopupConfirmButton.value = true;
                showInfoPopupCancelButton.value = false;
                infoConfirmText.value = 'OK';
                showInfoPopup.value = true;
                return;
            }
            pendingDeleteNode = node;
            infoTitle.value = 'Delete Page';
            infoMessage.value = `Delete "${node.name}" and its content?`;
            showInfoPopupConfirmButton.value = true;
            showInfoPopupCancelButton.value = true;
            infoConfirmText.value = 'Delete';
            infoCancelText.value = 'Cancel';
            showInfoPopup.value = true;
        }

        async function deletePage(node) {
            isLoading.value = true;
            try {
                await deletePageByUserId(props.project.project_id, node.page_id || node.id);
                await loadPages();
            } catch (err) {
                console.error('Failed to delete page:', err);
                infoTitle.value = 'Error!';
                infoMessage.value = 'Failed to delete page. Please try again.';
                showInfoPopupConfirmButton.value = true;
                showInfoPopupCancelButton.value = false;
                infoConfirmText.value = 'OK';
                showInfoPopup.value = true;
            } finally {
                pendingDeleteNode = null;
                isLoading.value = false;
            }
        }

        async function onInfoPopupConfirm() {
            if (pendingDeleteNode) {
                const node = pendingDeleteNode;
                await deletePage(node);
                return;
            }
            resetInfoPopupButtons();
        }

        function onInfoPopupCancel() {
            pendingDeleteNode = null;
            resetInfoPopupButtons();
        }

        function toggleProjectList() {
            emit('toggle');
        }

        const filteredPages = computed(() =>
            pages.value.filter(p => p.status !== 'deleted')
        );
        function findSiblings(node, list = pages.value) {
            for (const n of list) {
                if (n.children && n.children.length) {
                    const found = findSiblings(node, n.children);
                    if (found) return found;
                }
            }
            return list.findIndex(n => n.id === node.id) !== -1 ? list : null;
        }
        async function movePageUp(node) {
            const siblings = findSiblings(node);
            if (!siblings) return;
            const sorted = siblings.slice().sort((a, b) => a.page_order - b.page_order);
            const idx = sorted.findIndex(n => n.id === node.id);
            if (idx > 0) {
                await swapPageOrder(props.project.project_id, node.page_id, sorted[idx - 1].page_id);
                loadPages();
            }
        }
        async function movePageDown(node) {
            const siblings = findSiblings(node);
            if (!siblings) return;
            const sorted = siblings.slice().sort((a, b) => a.page_order - b.page_order);
            const idx = sorted.findIndex(n => n.id === node.id);
            if (idx < sorted.length - 1) {
                console.log(node)
                await swapPageOrder(props.project.project_id, node.page_id, sorted[idx + 1].page_id);
                loadPages();
            }
        }

        onMounted(loadPages);

        return {
            pages,
            isLoading,
            selectedPage,
            selectPage,
            addPage,
            loadPages,
            addChildPage: addChildPageHandler,
            toggleProjectList,
            filteredPages,
            movePageUp,
            movePageDown,
            requestDeletePage,
            showInputPopup,
            popupTitle,
            popupFields,
            onInputPopupConfirm,
            showAddPagePopup,
            showInfoPopup,
            infoTitle,
            infoMessage,
            showInfoPopupConfirmButton,
            showInfoPopupCancelButton,
            infoConfirmText,
            infoCancelText,
            onInfoPopupConfirm,
            onInfoPopupCancel,
        };
    }
});
</script>

<style scoped>
.project-management-panel {
    flex: 1 1 auto;
    padding: 1rem;
    background-color: var(--background-white);
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.project-panel-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.project-selector {
    font-weight: 600;
    margin-bottom: 1rem;
}

.page-tree {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0;
    margin: 0;
    list-style: none;
    padding-left: 0;
}

.page-tree>li {
    padding-left: 0;
}

.page-tree>li>.page-tree {
    margin-left: 16px;
}

.empty-page-message {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
}

.btn-full-width {
    margin-top: auto;
    width: 100%;
    padding: 10px;
    background-color: #F2F4F7;
    border: none;
    cursor: pointer;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}

.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--primary-color, #3498db);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.project-panel-inner.disabled {
    pointer-events: none;
    opacity: 0.5;
}

.page-tree li {
    padding-left: 0 !important;
}

.page-tree>li>ul>li {
    padding-left: 16px !important;
}
</style>
